import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { strings } from "../../resources/strings";
import { styles } from "../../shared-styles/styles";
import { langAttribute } from "../../utils/questionUtils";
import { useResourcesParticipantWebappFlag } from "../../utils/launchdarkly/featureFlags";
// Imported by direct path, not the components barrel: the barrel pulls in
// Navbar, which imports the routes table, which imports this page.
import LoadingIndicator from "../../components/shared/LoadingIndicator";
import ResourceCard from "../../components/Resources/ResourceCard";
import CategoryFilter from "../../components/Resources/CategoryFilter";
import ResourceSearch from "../../components/Resources/ResourceSearch";
import { useDebouncedValue } from "../../utils/useDebouncedValue";
import { useAgencyResources } from "../../components/Resources/hooks/useAgencyResources";
import {
  categoryOptions,
  filterResources,
  shouldShowCategoryPicker,
} from "../../components/Resources/resourceUtils";
import {
  listHistoryState,
  listSearch,
  readListState,
} from "../../components/Resources/resourceListState";

type Category = { label: string; locale: string | null };

/** How long typing has to pause before the result count is announced. */
export const SEARCH_ANNOUNCE_DELAY_MS = 500;

/**
 * Fills `%1s` (count), `%2s` (category) and `%3s` (search text) into a
 * template. The category label can be a fallback in another language, so it
 * keeps its own `lang` inside the sentence (SC 3.1.2); the search text is the
 * participant's own and is rendered as text.
 */
const fill = (
  template: string,
  values: { count?: number; category?: Category; query?: string },
): React.ReactNode =>
  template.split(/(%[123]s)/).map((part, index) => {
    if (part === "%1s") return String(values.count ?? "");
    if (part === "%3s") return values.query ?? "";
    if (part === "%2s" && values.category)
      return (
        <span key={index} lang={langAttribute(values.category.locale)}>
          {values.category.label}
        </span>
      );
    return part;
  });

const resultCountText = (
  count: number,
  category?: Category,
): React.ReactNode => {
  const { resources } = strings;
  const template = category
    ? count === 1
      ? resources.resultCountOneInCategory
      : resources.resultCountManyInCategory
    : count === 1
      ? resources.resultCountOne
      : resources.resultCountMany;
  return fill(template, { count, category });
};

const searchResultText = (
  count: number,
  query: string,
  category?: Category,
): React.ReactNode => {
  const { resources } = strings;
  let template: string;
  if (count === 0) {
    template = category ? resources.noMatchInCategory : resources.noMatch;
  } else if (category) {
    template =
      count === 1
        ? resources.searchCountOneInCategory
        : resources.searchCountManyInCategory;
  } else {
    template =
      count === 1 ? resources.searchCountOne : resources.searchCountMany;
  }
  return fill(template, { count, category, query });
};

export const ResourcesContent: React.FC = () => {
  const { resources, loaded, loading, error } = useAgencyResources();
  const location = useLocation();
  const navigate = useNavigate();
  const listState = readListState(location.search, location.state);
  // The category lives in the URL, so Back and Forward change it too.
  const { categoryId, returnFocusId } = listState;
  // Read from history once: the input owns the text after that, so writing
  // it back to history never pushes stale text into the field.
  const [query, setQuery] = useState(listState.query);
  // What this page last saved to history, to tell its own writes apart from
  // Back or Forward landing on another list entry with a different search.
  const lastWrittenQuery = useRef(listState.query);
  useEffect(() => {
    if (listState.query !== lastWrittenQuery.current) {
      lastWrittenQuery.current = listState.query;
      setQuery(listState.query);
    }
  }, [listState.query]);
  const linkRefs = useRef(new Map<string, HTMLAnchorElement>());
  const searchRef = useRef<HTMLInputElement>(null);
  // The count is only announced after the participant filters: a live region
  // that mounts already holding text is not reliably announced
  // (A11Y_CHECKLIST 4.1.3).
  const [hasFiltered, setHasFiltered] = useState(false);

  const options = useMemo(() => categoryOptions(resources), [resources]);
  const showPicker = shouldShowCategoryPicker(options);
  const activeOption = showPicker
    ? options.find((option) => option.id === categoryId)
    : undefined;
  const activeCategoryId = activeOption?.id ?? null;

  /** Replaces this history entry, keeping the category and search in step. */
  const writeListState = useCallback(
    (next: {
      categoryId: string | null;
      query: string;
      returnFocusId?: string;
    }) => {
      lastWrittenQuery.current = next.query;
      navigate(
        { pathname: location.pathname, search: listSearch(next.categoryId) },
        {
          replace: true,
          state: listHistoryState(next.query, next.returnFocusId),
        },
      );
    },
    [navigate, location.pathname],
  );

  // A category that disappears on refetch falls back to all resources, and
  // the stale selection is cleared so it cannot silently return later. Only
  // once the list has loaded: before that every category looks unknown, and
  // a refresh would lose the one in the URL.
  useEffect(() => {
    if (loaded && categoryId !== null && !activeOption) {
      writeListState({ categoryId: null, query, returnFocusId });
    }
  }, [loaded, categoryId, activeOption, query, returnFocusId, writeListState]);

  const search = query.trim();
  const visible = useMemo(
    () => filterResources(resources, activeCategoryId, search),
    [resources, activeCategoryId, search],
  );

  // Typing is announced once it pauses, so a screen reader is not interrupted
  // on every key. The announcement only moves on once the search has settled
  // (it is empty, or typing has paused); until then the last one stays, so
  // nothing new is read out. Clearing the search settles at once. Stored as a
  // key in state (React's "adjust state during render" pattern), not a ref,
  // so a render that is never committed cannot leave a stale sentence behind.
  const debouncedSearch = useDebouncedValue(search, SEARCH_ANNOUNCE_DELAY_MS);

  // The search text goes into this history entry's state once typing pauses,
  // never into the URL (see resourceListState).
  const debouncedQuery = useDebouncedValue(query, SEARCH_ANNOUNCE_DELAY_MS);
  const lastDebouncedQuery = useRef(debouncedQuery);
  useEffect(() => {
    // Only when the settled text changes. Back or Forward to another entry
    // changes listState.query too, and must not save this text onto it.
    if (debouncedQuery === lastDebouncedQuery.current) return;
    lastDebouncedQuery.current = debouncedQuery;
    if (debouncedQuery !== listState.query) {
      writeListState({ categoryId, query: debouncedQuery, returnFocusId });
    }
  }, [
    debouncedQuery,
    listState.query,
    categoryId,
    returnFocusId,
    writeListState,
  ]);

  // Back from a resource focuses the card that was opened, once it is on
  // screen, then forgets it so a later re-render does not steal focus.
  useEffect(() => {
    if (!returnFocusId || !loaded) return;
    linkRefs.current.get(returnFocusId)?.focus();
    // A category that has since gone is dropped here too, not written back.
    writeListState({
      categoryId: activeOption ? categoryId : null,
      query,
      returnFocusId: undefined,
    });
  }, [returnFocusId, loaded, categoryId, activeOption, query, writeListState]);
  const settled = !search || debouncedSearch === search;
  const [announced, setAnnounced] = useState<{
    count: number;
    categoryId: string | null;
    search: string;
  } | null>(null);
  if (
    hasFiltered &&
    settled &&
    (announced?.count !== visible.length ||
      announced.categoryId !== activeCategoryId ||
      announced.search !== search)
  ) {
    setAnnounced({
      count: visible.length,
      categoryId: activeCategoryId,
      search,
    });
  }

  const handleCategoryChange = (nextCategoryId: string | null) => {
    writeListState({ categoryId: nextCategoryId, query, returnFocusId });
    setHasFiltered(true);
  };
  const handleSearchChange = (next: string) => {
    setQuery(next);
    // Spaces alone change nothing on screen, so they are not announced.
    if (next.trim() !== search) setHasFiltered(true);
  };
  const clearSearch = () => {
    setQuery("");
    setHasFiltered(true);
  };
  const clearSearchAndFocus = () => {
    clearSearch();
    searchRef.current?.focus();
  };
  const searchAllCategories = () => {
    writeListState({ categoryId: null, query, returnFocusId });
    setHasFiltered(true);
    searchRef.current?.focus();
  };

  let announcement: React.ReactNode = "";
  if (announced) {
    const category = options.find(
      (option) => option.id === announced.categoryId,
    );
    announcement = announced.search
      ? searchResultText(announced.count, announced.search, category)
      : resultCountText(announced.count, category);
  }
  const hasResources = resources.length > 0;

  return (
    <main id="main-content" data-theme="repath">
      <div className={styles.pageHeader}>
        <h1>{strings.pages.resources}</h1>
      </div>

      <div className="w-full max-w-4xl mx-auto mt-4 flex flex-col gap-4">
        <p
          role="status"
          aria-atomic="true"
          className="sr-only"
          data-testid="resources-result-count"
        >
          {announcement}
        </p>

        {loading && !loaded && (
          <div className="flex justify-center py-10">
            <LoadingIndicator loadingText={strings.resources.loading} />
          </div>
        )}

        {/* A failed background refresh keeps the cached list on screen without
            an error: the participant can still use what was already loaded. */}
        {error && !hasResources && (
          <p role="alert" className="text-red-700">
            {strings.resources.loadError}
          </p>
        )}

        {loaded && !hasResources && (
          // Plain text, read in page order: a status region that mounts already
          // holding its text is not reliably announced (see the count above).
          <p className="text-gray-700">{strings.resources.empty}</p>
        )}

        {hasResources && (
          <ResourceSearch
            value={query}
            onChange={handleSearchChange}
            onClear={clearSearch}
            inputRef={searchRef}
          />
        )}

        {hasResources && showPicker && (
          <CategoryFilter
            options={options}
            value={activeCategoryId}
            onChange={handleCategoryChange}
          />
        )}

        {/* Plain text: the status region above announces it once typing pauses. */}
        {hasResources && search && visible.length === 0 && (
          <div className="flex flex-col items-start gap-3">
            <p className="text-gray-700 [overflow-wrap:anywhere]">
              {searchResultText(0, search, activeOption)}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline btn-sm min-h-11"
                onClick={clearSearchAndFocus}
              >
                {strings.resources.clearSearch}
              </button>
              {activeOption && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm min-h-11"
                  onClick={searchAllCategories}
                >
                  {strings.resources.searchAllCategories}
                </button>
              )}
            </div>
          </div>
        )}

        {hasResources && visible.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {visible.map((resource) => (
              <li key={resource.id}>
                <ResourceCard
                  resource={resource}
                  to={`/resources/${encodeURIComponent(resource.id)}`}
                  linkState={{
                    fromList: true,
                    listSearch: listSearch(categoryId),
                  }}
                  // Saves the live search and the card on this entry first,
                  // so Back lands on the same list with this card focused.
                  onOpen={() =>
                    writeListState({
                      categoryId,
                      query,
                      returnFocusId: resource.id,
                    })
                  }
                  linkRef={(element) => {
                    if (element) linkRefs.current.set(resource.id, element);
                    else linkRefs.current.delete(resource.id);
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
};

/**
 * Flag gate only. Kept separate from the content so the resources query never
 * runs for a participant who cannot see the page.
 */
const Resources: React.FC = () => {
  const enabled = useResourcesParticipantWebappFlag();
  if (!enabled) {
    return <Navigate to="/" replace />;
  }
  return <ResourcesContent />;
};

export default Resources;
